import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { media, layout, remCalc } from '@prisjakt/ui-base';
import { withViewport } from '@prisjakt/ui-viewport';
import BaseCoreProperties from '@prisjakt/ui-core-properties';
import BaseRating from '@prisjakt/ui-rating';
import { H2Text, SubtitleText } from '@prisjakt/ui/text/variants';

export const ProductPage = styled.div.attrs({ 'data-test': 'ProductPage' })`
  position: relative;

  /* This is needed or the review action dropdown will be obscured */
  .pj-ui-panel--row {
    overflow: visible;
  }

  .pj-ui-scroll-link {
    button:hover {
      color: ${p => p.theme.text.callToActionHover};
      text-decoration: underline;
    }
  }

  .pj-ui-price-row {
    width: 100%;
  }

  .product-header--content {
    flex-direction: row;
  }
`;

export const ProductSummary = styled.div`
  flex: 0 0 auto;
  width: 300px;
  margin-right: 10px;

  ${media.smallOnly`
    flex: 1 0 auto;
    margin-right: 0;
    width: auto;
    padding: 1rem;
    background-color: ${p => p.theme.colors.base100};
    margin-bottom: 0.85rem;
  `}
`;

export const ProductActions = styled.div`
  margin: 10px 0 20px 0;
`;

export const CategoryRank = styled.div`
  font-size: 14px;
  margin-top: 12px;
  color: ${p => p.theme.colors.base600};

  a {
    color: ${p => p.theme.colors.maybeBlue700};
    cursor: pointer;
    outline: none;

    &:hover,
    &:focus {
      color: ${p => p.theme.colors.maybeBlue500};
      text-decoration: underline;
    }
  }
`;

export const CategoryRankPill = styled.span`
  font-size: 12px;
  background: #a1d7ef;
  color: #23567a;
  font-weight: 600;
  text-transform: uppercase;
  padding: 2px 4px;
  border-radius: 2px;
  margin-bottom: 2px;
`;

export const CategoryRankNumber = styled.span`
  color: #000;
`;

const backToTopButtonDropShadow = '0 1px 3px rgba(0, 0, 0, 0.3)';

type BackToTopButtonProps = {
  viewport: {
    top: number;
    scroll: {
      yDirection: number;
    };
  };
};

const BackToTopButtonComponent = styled.button.attrs(() => ({
  type: 'button',
}))<BackToTopButtonProps>`
  position: fixed;
  bottom: 10px;
  ${media.hideOutsiderAd`
    right: 10px;
  `};
  ${media.showOutsiderAd`
    left: 50%;
    margin-left: ${layout.siteWidth.value * 0.5 - 200}px;
  `};
  background: ${p => p.theme.colors.yellove500};
  border-radius: ${layout.borderRadius};
  box-shadow: ${backToTopButtonDropShadow};
  border: 1px solid ${p => p.theme.colors.yellove500};
  cursor: pointer;
  padding: 10px 12px;
  visibility: ${props =>
    props.viewport &&
    props.viewport.top > 500 &&
    props.viewport.scroll.yDirection > 0
      ? 'visible'
      : 'hidden'};
  opacity: ${props =>
    props.viewport &&
    props.viewport.top > 500 &&
    props.viewport.scroll.yDirection > 0
      ? 0.9
      : 0};
  transition: opacity 0.2s linear;
  z-index: 4;

  :hover {
    background-color: ${p => p.theme.colors.yellove500};
  }

  :focus {
    background-color: ${p => p.theme.colors.yellove500};
    box-shadow: ${backToTopButtonDropShadow}, 0 0 0 4px #000;
    opacity: 1;
  }

  :focus:not(:focus-visible) {
    box-shadow: ${backToTopButtonDropShadow};
  }
`;

export const BackToTopButton = withViewport(BackToTopButtonComponent);

export const Rating = styled.div`
  cursor: pointer;
  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

export const RatingWrapper = styled.div`
  margin: 0.5em 0;
  text-decoration: underline;
  color: ${p => p.theme.text.callToAction};

  span {
    color: inherit;
  }
  &:hover span {
    color: ${p => p.theme.text.callToActionHover};
  }
`;

export const WriteReview = styled.div`
  cursor: pointer;
  color: ${p => p.theme.text.linkExternal};

  &:hover,
  &:focus {
    color: ${p => p.theme.text.linkExternalHover};
    text-decoration: underline;
  }
`;

export const Description = styled.div`
  font-size: ${remCalc(14)};
  max-width: 500px;
  margin-top: ${remCalc(12)};
  color: ${p => p.theme.text.secondary};

  ul {
    list-style-position: inside;
  }
`;

export const RelatedPage = styled.div`
  font-size: ${remCalc(14)};
  margin-top: ${remCalc(14)};
  color: ${p => p.theme.text.secondary};

  a,
  .pj-ui-scroll-link {
    color: ${p => p.theme.text.callToAction};
    cursor: pointer;
    outline: none;
    &:hover,
    &:focus {
      color: ${p => p.theme.text.callToActionHover};
      text-decoration: underline;
    }
  }
`;

export const Modal = styled.div`
  margin-top: 30px;
  width: 100%;
`;

export const ReviewBadges = styled.div`
  margin: 0.5em 0;
`;

export const More = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${media.mediumDown`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  `};
`;

export const SpecificationsLink = styled.span`
  color: ${p => p.theme.text.callToAction};
  cursor: pointer;
  outline: none;
  &:hover,
  &:focus {
    color: ${p => p.theme.text.callToActionHover};
    text-decoration: underline;
  }
`;

export type TabList = {
  isSticky: boolean;
};

export const TabList = styled.div<TabList>`
  background: #e4eff4;
  padding: 5px;
  display: flex;
  margin-bottom: 15px;
  justify-content: center;
  transition: border-radius 0.1s linear, box-shadow 0.2s linear;

  ${media.largeUp`
    border-radius: 10px;
    margin-left: ${layout.pageContent.gutter.rem};
    margin-right: ${layout.pageContent.gutter.rem};
  `};

  ${props =>
    props.isSticky &&
    css`
      box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1);

      ${media.largeUp`
        border-radius: 0 0 10px 10px;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      `}
    `};
`;

type LinkedTabProps = {
  active: boolean;
  to: string;
};

const LinkedTab = ({ active, ...props }: LinkedTabProps) => {
  return <Link {...props} />;
};

const tabTitleLineHeight = layout.lineHeight;
const tabTitleFontSize = { desktop: 14, mobile: 10 };

export const TabTitle = styled.div`
  flex: 0 0 100%;
  font-weight: 600;
  font-size: ${remCalc(tabTitleFontSize.mobile)};
  line-height: ${tabTitleLineHeight}em;
  opacity: 0.7;
  transition: opacity 0.1s linear;

  ${media.largeUp`
    font-size: ${remCalc(tabTitleFontSize.desktop)};
  `};
`;

const highlightedTabTitleStyle = css`
  opacity: 1;
`;

const tabSubtitleLineHeight = 1.2;
const tabSubtitleFontSize = 10;
const tabSubtitleHeight = tabSubtitleFontSize * tabSubtitleLineHeight * 2;

export const TabSubtitle = styled.div`
  font-size: ${remCalc(tabSubtitleFontSize)};
  flex: 0 0 100%;
  height: ${remCalc(tabSubtitleHeight)};
  line-height: ${tabSubtitleLineHeight}em;
  opacity: 0.5;
  transition: all 0.1s linear;

  display: flex;
  justify-content: center;
  align-items: center;
`;

const highlightedTabSubtitleStyle = css`
  opacity: 1;
`;

export const TabLowestPrice = styled(TabSubtitle)`
  font-size: 12px;
  font-weight: 600;
`;

const highlightedTabLowestPriceStyle = css`
  color: ${p => p.theme.colors.negative700};
`;

export const TabRating = styled(TabSubtitle)`
  filter: grayscale(1);
`;

const highlightedTabRatingStyle = css`
  filter: none;
`;

export const TabSparkline = styled(TabSubtitle)`
  filter: grayscale(1);
`;

const highlightedTabSparklineStyle = css`
  filter: none;
`;

export const Tab = styled(LinkedTab)<LinkedTabProps>`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  flex: 1 1 auto;
  padding: 6px 10px;
  background: ${props =>
    props.active ? props.theme.colors.base0 : 'transparent'};
  border-radius: 5px;
  border-bottom: 0 none;
  text-align: center;
  color: ${props => props.theme.colors.base700};
  cursor: ${props => (props.active ? 'default' : 'pointer')};
  hyphens: auto;

  :hover,
  :focus {
    ${TabTitle} {
      ${highlightedTabTitleStyle}
    }
    ${TabSubtitle} {
      ${highlightedTabSubtitleStyle}
    }
    ${TabLowestPrice} {
      ${highlightedTabLowestPriceStyle}
    }
    ${TabRating} {
      ${highlightedTabRatingStyle}
    }
    ${TabSparkline} {
      ${highlightedTabSparklineStyle}
    }
  }

  ${props =>
    props.active &&
    css`
      ${TabTitle} {
        ${highlightedTabTitleStyle}
      }
      ${TabSubtitle} {
        ${highlightedTabSubtitleStyle}
      }
      ${TabLowestPrice} {
        ${highlightedTabLowestPriceStyle}
      }
      ${TabRating} {
        ${highlightedTabRatingStyle}
      }
      ${TabSparkline} {
        ${highlightedTabSparklineStyle}
      }
    `}

  ${media.largeUp`
    flex: 1 0 0;
  `};
`;

type TabContentWrapperProps = {
  spacingTop: boolean;
};

export const TabContentWrapper = styled.div<TabContentWrapperProps>`
  margin-top: ${p => (p.spacingTop ? remCalc(16) : 0)};
  padding: 0 ${layout.pageContent.gutter.rem};
`;

export const TabContent = styled.div``;

export const StyledCoreProperties = styled(BaseCoreProperties)`
  && {
    color: inherit;
    margin: 0;
  }
`;

export const StyledRating = styled(BaseRating)`
  && {
    font-size: inherit;
  }
`;

export const StoreLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  ${media.smallOnly`
    img {
      max-width: 77px;
    }
  `};
`;

export const NewStoreLogo = styled.div`
  && {
    display: block;
    position: relative;
    text-align: center;

    img {
      max-width: 77px;
    }
    ${media.smallOnly`
      img {
        max-width: 44px;
      }
    `};
  }
`;

export const BreadCrumbWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

type HCProps = { visible?: boolean };
export const HideableContent = styled.div<HCProps>`
  display: ${props => (props.visible ? 'block' : 'none')};
`;

export const ReviewsWrapper = styled.section`
  display: flex;
  flex-direction: row;

  @supports (display: grid) {
    display: grid;
    grid-template-columns: 70% 30%;
    column-gap: ${layout.measurements.spacingSmall};
  }

  ${media.mediumDown`
    @supports (display: grid) {
      grid-template-columns: 1fr;
      row-gap: ${layout.measurements.spacingMedium};
    }
    
    flex-direction: column;
  `};
`;

export const Reviews = styled.main`
  padding: 0 ${layout.measurements.spacingSmall};
  width: 70%;
  order: 0;

  @supports (display: grid) {
    width: auto;
  }

  ${media.mediumDown`
    width: 100%;
    order: 1;
  `};
`;

export const ReviewsSidebar = styled.aside`
  padding: 0 ${layout.measurements.spacingSmall};
  width: 30%;
  order: 1;

  @supports (display: grid) {
    width: auto;
  }

  ${media.mediumDown`
    width: 100%;
    order: 0;
  `};
`;

export const ExpertContent = styled.div`
  margin-top: ${layout.measurements.spacingMedium};
`;

export const Title = styled(H2Text)`
  display: block;
  font-weight: 700;
  margin-bottom: ${layout.measurements.spacingNormal};
`;

export const Subtitle = styled(SubtitleText)`
  display: block;
  font-weight: 700;
  margin-bottom: ${layout.measurements.spacingNormal};
`;
